import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Avatar, Splitter } from 'antd';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Chat from './Chat/ChatContainer';
import Notes from './Notes/NotesContainer';
import * as actions from '../../store/actions';
import * as streamActions from '../../store/actions/InterviewRecordingActions';
import {
  setCurrentLanguageforProblems,
  subscribeToRemoteAudio,
  updateCodeAndLanguageForProblem,
} from '../../store/actions/InterviewActions';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { history } from '../../index';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { isCookieExpired, isInterviewDeveloper, isInterviewCandidate } from '../../helperFunctions';
import styles from './InterviewContainer.scss';
import ProblemTabs from './ProblemTabs.js';
import { getCodingProblems } from '../../store/actions/InterviewActions';
import { setProblemDetails } from '../../store/reducers/ProblemReducer';
import DriverCodeModal from '../../components/DriverCodeModal';
import VideoCallUI from '../../components/Interview/PerticepentsVideoContainer.js';
import MeetingControlBar from '../../components/Meeting/MeetingControlBar.js';
import ScreenShareView from '../Interview/Meeting/ScreenShareView.js';
import { leaveMeeting } from '../../Utils/utils.js';
import eventEmitter, { EVENT_TYPES } from '../../EventEmitterService';
import { networkStatusChecker } from '../../Utils/networkStatusChecker';
import { store } from '../../index.js';
import { useInterviewCommonLogic } from '../../hooks/useInterviewCommonLogic';

import {
  RECONNECTION_TIMEOUT,
  RECONNECTION_ATTEMPT,
  DEFAULT_TEMPLATES,
  FREE_FORM_EDITOR_ID,
} from '../../const';

const InterviewContainer = props => {
  const { t } = useTranslation('interview_container');
  const [editorExpand, setEditorExpand] = useState(null); // null or problem_id
  const [execute_status, setExecuteStatus] = useState('');
  const [showOutput, setShowOutput] = useState(false);
  const [maximizeOutput, setMaximizeOutput] = useState(false);
  const [syncCursor, setSyncCursor] = useState(false);
  const [curr_row, setCurrRow] = useState('');
  const [curr_typing_user, setCurrTypingUser] = useState('');
  const [editor, setEditor] = useState(null);
  const [activeTabId, setActiveTabId] = useState(null);
  const [isProblemTabActive, setIsProblemTabActive] = useState(false);
  const [freeFormOutput, setFreeFormOutput] = useState(null);
  const [showDriverCodeModal, setShowDriverCodeModal] = useState(false);
  const [driverCodeModalData, setDriverCodeModalData] = useState({
    lang: '',
    activeTemplate: null,
  });

  const toggleDriverCodeModal = (lang, activeTemplate) => {
    setDriverCodeModalData({ lang, activeTemplate });
    setShowDriverCodeModal(prev => !prev);
  };

  function upsert(arr, newItem, key = 'problem_id') {
    const exists = arr.some(item => item[key] === newItem[key]);
    return exists
      ? arr.map(item => (item[key] === newItem[key] ? newItem : item))
      : [...arr, newItem];
  }

  const xtermRef = useRef();

  const dispatch = useDispatch();

  const additionalMessageHandlers = {
    editor: message => editorUpdate(message.data),
    end_typing: message => endTyping(message.data),
    send_initial_cursor_position: () => sendInitialCursorPosition(),
    receive_initial_cursor_position: message => setIntitialCursorPosition(message.data),
    presence: message => handlePresence(message.data),
    execute: message => showOutputHandler(message.data),
    language: message => languageUpdate(message.data),
    coding_problem_visibility_change: message => handleVisibilityChange(message),
    test_case_execution_complete: message => handleExecutionStatus(message.data),
    code_execution_complete: message => handleCompletedExecution(message.data),
    active_tab_change: message => handleActiveTabChange(message.data),
  };

  const options = { enableReconnection: true };

  const {
    chat,
    currOption,
    setCurrOption,
    isWSConnected,
    isScreenShareView,
    curr_note,
    setCurrNoteState,
    inactivityModalVisible,
    countdown,
    contextHolder,
    participants_status,
    sendMessage,
    toggleView,
    handleRecoveryModalOk,
    handleContinue,
    handleDisconnect,
    handleEndInterview,
    wsRef,
    interview_id,
  } = useInterviewCommonLogic(props, additionalMessageHandlers, options);

  useEffect(() => {
    if (props.problemDetails.length == 0) return;
    const LanguageforProblems = props.problemDetails.map(item => {
      const defaultTemplate = item.problem_details?.templates?.find(
        template => template.language === 'python3',
      );
      return {
        problem_id: item.problem_id,
        language: 'python3',
        code: defaultTemplate?.body || '',
      };
    });
    dispatch(setCurrentLanguageforProblems(LanguageforProblems));
  }, [props.problemDetails]);

  useEffect(() => {
    if (!props.isVideoEnabled && props.isVideoEnabled) {
      setCurrOption('video');
    }
    if (props.isVideoEnabled && !props.isVideoEnabled) {
      setCurrOption('meeting');
    }
  }, [props.isVideoEnabled]);

  useEffect(() => {
    if (!props.isScreenSharing && props.isScreenSharing) {
      props.setEditorView('screen-share');
    }
    if (props.isScreenSharing && !props.isScreenSharing) {
      props.setEditorView('editor');
    }
  }, [props.isScreenSharing]);

  useEffect(() => {
    if (props.meetingSession && props.participants && props.participants.length > 0) {
      const unsubscribe = props.subscribeToRemoteAudio();
      return unsubscribe;
    }
  }, [props.meetingSession, props.participants]);

  const handleVisibilityChange = message => {
    if (message.data && message.data.problem_id && Array.isArray(props.problemDetails)) {
      const problemExists = props.problemDetails.some(
        problem => problem.problem_details.id.toString() === message.data.problem_id.toString(),
      );
      if (problemExists) {
        const updatedProblemDetails = props.problemDetails.map(problem => {
          if (problem.problem_details.id.toString() === message.data.problem_id.toString()) {
            return {
              ...problem,
              visibility_status: message.data.visibility_status,
            };
          }
          return problem;
        });
        props.setProblemDetails(updatedProblemDetails);
      } else {
        props.getCodingProblems(interview_id);
      }
    } else {
      console.log('Invalid message data or problemDetails not array');
    }
  };

  const handlePresence = data => {
    if (props.participants) {
      let participants = props.participants.map(p => {
        let online = data.some(d => d === p.id);
        return {
          ...p,
          isActive: online,
        };
      });
      props.setParticipants(participants);
    }
  };

  const sendInitialCursorPosition = () => {
    let position = editor?.getPosition();
    if (
      position &&
      position.row !== '' &&
      position.row !== null &&
      position.row === parseInt(position.row, 10) &&
      position.row >= 0 &&
      position.column !== '' &&
      position.column !== null &&
      position.column === parseInt(position.column, 10) &&
      position.column >= 0
    ) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'send_initial_cursor_position',
            data: position,
          }),
        );
      }
    }
  };

  const isAttendeeInterviewer = tileState => {
    if (tileState) {
      return !tileState.localTile && !tileState.isContent;
    }
    return false;
  };

  const canSync = cursor => {
    if (!syncCursor) return false;
    if (
      cursor.row === null ||
      cursor.row === '' ||
      cursor.column === null ||
      cursor.column === ''
    ) {
      return false;
    }
    if (cursor.row !== parseInt(cursor.row, 10)) return false;
    if (cursor.column !== parseInt(cursor.column, 10)) return false;
    if (cursor.row < 0 || cursor.column < 0) return false;
    return true;
  };

  const setIntitialCursorPosition = data => {
    if (data && canSync(data)) editor.gotoLine(data.row + 1, data.column, true);
  };

  const editorUpdate = data => {
    dispatch(
      updateCodeAndLanguageForProblem({
        problem_id: data.problem_id || FREE_FORM_EDITOR_ID,
        code: data.data,
        language: data.language,
      }),
    );
    if (data.cursor && canSync(data.cursor))
      editor.gotoLine(data.cursor.row + 1, data.cursor.column, true);
    let user_name = props.participants?.find(item => item.email === data.user.email)?.name;
    user_name = user_name?.split(' ')[0];
    user_name = user_name?.substring(0, 15);
    if (!curr_typing_user) {
      setCurrTypingUser(user_name);
    }
  };

  const languageUpdate = data => {
    if (!data?.problem_id) {
      return;
    }

    let templateBody = '';
    // For regular problems, find template from problem details
    const problem = props.problemDetails.find(p => p.problem_id === data.problem_id);
    const template = problem?.problem_details?.templates?.find(t => t.language === data.language);
    templateBody = template?.body || '';
    props
      .fetchLatestEditorEvent(interview_id, data.language, data.problem_id, templateBody)
      .then(res => {
        console.log(props.last_editor_data, 'lastdata');
      });
  };
  let executedData = [];
  const handleExecutionStatus = data => {
    executedData.push(data);
  };

  const handleCompletedExecution = data => {
    props.setResults(executedData);
    props.setLoadingResults(false);
    executedData = [];
  };

  const handleActiveTabChange = data => {
    setActiveTabId(data.to);
    // If switching from empty tab, it means a new problem was added, fetch updated problems
    if (data.from === '') {
      props.getCodingProblems(interview_id);
    }
  };

  const showOutputHandler = data => {
    setExecuteStatus(data.status);
    setShowOutput(true);
    setFreeFormOutput(data); // Update free form editor output
    if (xtermRef.current) {
      xtermRef.current.terminal.write(
        data.stdout.replace(/\n/g, '\r\n') + data.stderr.replace(/\n/g, '\r\n'),
      );
    }
  };

  const closeOutput = () => {
    setShowOutput(false);
    setMaximizeOutput(false);
  };

  const endTyping = () => {
    setCurrTypingUser('');
  };

  const submitCode = (code, lang, problem_id, data) => {
    props.setLoadingResults(true);
    props.submitCode(code, lang, problem_id, data);
  };

  const submitFreeFormEditorCode = (code, lang) => {
    props.setLoadingResults(true);
    props.submitFreeFormEditorCode(code, lang);
  };

  const onCodeInEditorChange = (code, selectedLanguage, problem_id) => {
    dispatch(updateCodeAndLanguageForProblem({ problem_id, code, language: selectedLanguage }));
    let cursor;
    let position = editor?.getPosition();
    if (
      position &&
      position.row !== '' &&
      position.row !== null &&
      position.row === parseInt(position.row, 10) &&
      position.row >= 0 &&
      position.column !== '' &&
      position.column !== null &&
      position.column === parseInt(position.column, 10) &&
      position.column >= 0
    )
      cursor = position;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'editor',
          data: {
            language: selectedLanguage,
            cursor: cursor ? cursor : '',
            data: code,
            ...(problem_id !== FREE_FORM_EDITOR_ID && { problem_id }),
          },
        }),
      );
    }
    handleEndTyping();
  };

  const toggleExpand = () => {
    setEditorExpand(prev => !prev);
  };

  const switchLanguage = (language, problem_id) => {
    if (!problem_id) {
      return;
    }
    // Find the problem and get the template for the new language
    const problem = props.problemDetails.find(p => p.problem_id === problem_id);
    const template = problem?.problem_details?.templates?.find(t => t.language === language);
    const templateBody = template?.body || '';

    props.fetchLatestEditorEvent(interview_id, language, problem_id, templateBody).then(res => {
      console.log(props.last_editor_data, 'lastdata');
    });
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const messageData = {
        language: language,
      };
      if (problem_id !== FREE_FORM_EDITOR_ID) {
        messageData.problem_id = problem_id;
      }
      wsRef.current.send(
        JSON.stringify({
          type: 'language',
          data: messageData,
        }),
      );
    }
  };

  const editorCollapse = () => {
    setEditorExpand(false);
  };

  const setOption = option => {
    setCurrOption(option);
  };

  const resizeOutputView = () => {
    setMaximizeOutput(prev => {
      const newValue = !prev;
      if (newValue) {
        xtermRef.current.terminal.resize(110, 33);
      } else {
        xtermRef.current.terminal.resize(110, 7);
      }
      return newValue;
    });
  };

  const handleEndTyping = useCallback(
    debounce(function () {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'end_typing',
            data: {},
          }),
        );
      }
    }, 2000),
    [],
  );

  const syncCursorHandler = checked => {
    setSyncCursor(checked);
    editor.focus();
    if (checked) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'get_initial_cursor_position',
            data: {},
          }),
        );
      }
    }
  };

  const mode = {
    python3: 'python',
    javascript: 'javascript',
    c: 'c',
    cpp: 'cpp',
    java: 'java',
    ruby3: 'ruby',
  };

  let editorTabSpan = 18;
  let chatTabSpan = 6;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 86px)',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isScreenShareView ? (
          <ScreenShareView />
        ) : (
          <div
            className={styles['c-interview']}
            style={{
              height: '100%',
              overflow: 'hidden',
              background: '#F5F5F5',
              paddingTop: '16px',
            }}
          >
            <Splitter
              size={0}
              style={{ height: '100%' }}
            >
              <Splitter.Panel
                size={
                  editorExpand && isProblemTabActive && editorExpand === activeTabId ? '80%' : '75%'
                }
                min="0%"
                style={{ overflow: 'hidden', padding: '0' }}
              >
                <ProblemTabs
                  problemDetails={props.problemDetails}
                  isDeveloper={isInterviewDeveloper(props.participants)}
                  wsRef={wsRef}
                  getCodingProblems={props.getCodingProblems}
                  setProblemDetails={props.setProblemDetails}
                  interview_id={interview_id}
                  onCodeInEditorChange={onCodeInEditorChange}
                  switchLanguage={switchLanguage}
                  submitCode={submitCode}
                  submitFreeFormCode={submitFreeFormEditorCode}
                  freeFormOutput={freeFormOutput}
                  editorExpand={editorExpand}
                  toggleExpand={problemId =>
                    setEditorExpand(prev => (prev === problemId ? null : problemId))
                  }
                  onActiveTabChange={(tabId, isProblem) => {
                    setActiveTabId(tabId);
                    setIsProblemTabActive(isProblem);
                  }}
                  onFreeFormOutputChange={() => props.setLoadingResults(false)}
                  onDriverCodeModalToggle={toggleDriverCodeModal}
                />
              </Splitter.Panel>
              <Splitter.Panel
                collapsible
                size={
                  editorExpand && isProblemTabActive && editorExpand === activeTabId ? '0%' : '20%'
                }
                min="0%"
                style={{ padding: '0' }}
              >
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: `${
                      isInterviewDeveloper(props.participants)
                        ? props.problemDetails && props.problemDetails.length > 0
                          ? '50px'
                          : '40px'
                        : '40px'
                    } 0 0 0`,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  {currOption === 'video' && (
                    <div
                      className={styles['c-interview__chat']}
                      style={{
                        flex: 1,
                        marginTop: 0,
                        background: 'white',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          background: 'white',
                          padding: '16px',
                          paddingLeft: 0,
                          height: '100%',
                          borderRadius: '8px',
                        }}
                      >
                        <VideoCallUI />
                      </div>
                    </div>
                  )}
                  {currOption === 'chat' && (
                    <div
                      className={styles['c-interview__chat']}
                      style={{
                        flex: 1,
                        marginTop: 0,
                        background: 'white',
                        overflow: 'hidden',
                      }}
                    >
                      <div className={styles['c-interview__chat']}>
                        <div
                          style={{
                            background: 'white',
                            padding: '16px',
                            paddingLeft: 0,
                            height: '100%',
                            borderRadius: '8px',
                          }}
                        >
                          <Chat
                            sendMessage={sendMessage}
                            chat={chat}
                            connected={isWSConnected}
                            chat_data={props.chat_data}
                            unreadCount={props.unreadCount}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {currOption === 'notes' && (
                    <div
                      className={styles['c-interview__chat']}
                      style={{
                        flex: 1,
                        marginTop: 0,
                        background: 'white',
                        overflow: 'hidden',
                      }}
                    >
                      <div className={styles['c-interview__chat']}>
                        <div
                          style={{
                            background: 'white',
                            padding: '16px',
                            paddingLeft: 0,
                            height: '100%',
                            borderRadius: '8px',
                          }}
                        >
                          <Notes
                            curr_note={curr_note}
                            setCurrNoteState={setCurrNoteState}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Splitter.Panel>
            </Splitter>
          </div>
        )}
      </div>
      <MeetingControlBar
        setCurrOption={setCurrOption}
        isDeveloper={isInterviewDeveloper(props.participants)}
        onScreenShareClick={() => toggleView()}
        onEndMeeting={() => {
          networkStatusChecker.stopMonitoring();
          props.endMeeting(isInterviewDeveloper(props.participants));
        }}
        isScreenShareActive={isScreenShareView}
        currOption={currOption}
      />
      {contextHolder}
      {props.recoveryModalOpen && (
        <Modal
          title={t('nt_interview_container.screen_share_stopped_title')}
          open={props.recoveryModalOpen}
          onOk={handleRecoveryModalOk}
          closable={false}
          okText={t('nt_interview_container.share_screen_again')}
        >
          <p>{t('nt_interview_container.screen_share_stopped_message')}</p>
        </Modal>
      )}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            {t('nt_interview_container.interviewer_left_meeting_title')}
          </div>
        }
        open={inactivityModalVisible}
        onOk={handleContinue}
        onCancel={handleDisconnect}
        okText={t('nt_interview_container.continue_interview')}
        cancelText={t('nt_interview_container.end_interview')}
        closable={false}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <p>{t('nt_interview_container.interviewer_left_meeting_message')}</p>
          <p>
            {t('nt_interview_container.time_remaining')}: <strong>{countdown}</strong>{' '}
            {t('nt_interview_container.seconds')}
          </p>
        </div>
      </Modal>
      <DriverCodeModal
        open={showDriverCodeModal}
        onCancel={() => setShowDriverCodeModal(false)}
        lang={driverCodeModalData.lang}
        activeTemplate={driverCodeModalData.activeTemplate}
        theme={props.theme || 'vs-light'}
      />
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    submitCode: (code, language, problem_id, data) =>
      dispatch(actions.submitInterviewCode(code, language, problem_id, data)),
    submitFreeFormEditorCode: (code, language) =>
      dispatch(actions.submitFreeFormCode(code, language)),
    fetchInterviewInvite: (
      interview_id,
      hash_value,
      role,
      company_id,
      fetch_interview_data,
      callback,
    ) =>
      dispatch(
        actions.fetchInterviewInvite(
          interview_id,
          hash_value,
          role,
          company_id,
          fetch_interview_data,
          callback,
        ),
      ),
    fetchData: interview_id => dispatch(actions.fetchData(interview_id)),
    setLastCode: data => dispatch(actions.setLastCode(data)),
    setLastLanguage: data => dispatch(actions.setLastLanguage(data)),
    getLanguages: () => dispatch(actions.getLanguages()),
    setEndInterviewUserDetail: user => dispatch(actions.setEndInterviewUserDetail(user)),
    setParticipants: participants => dispatch(actions.setParticipants(participants)),
    postNote: (data, callback) => dispatch(actions.postNote(data, callback)),
    fetchLatestEditorEvent: (interview_id, language, problem_id, code) =>
      dispatch(actions.fetchLatestEditorEvent(interview_id, language, problem_id, code)),
    checkNetworkConnection: () => dispatch(actions.checkNetworkConnection()),
    setHeartbeat: fire_time => dispatch(actions.setHeartbeat(fire_time)),
    setEditorView: data => dispatch(actions.setEditorView(data)),
    getCodingProblems: interview_id => dispatch(getCodingProblems(interview_id)),
    setProblemDetails: data => dispatch(setProblemDetails(data)),
    setResults: results => dispatch(actions.setResults(results)),
    setActiveTab: problem_id => dispatch(actions.setActiveTab(problem_id)),
    setLoadingResults: loading => dispatch(actions.setLoadingResults(loading)),
    subscribeToRemoteAudio: () => dispatch(subscribeToRemoteAudio()),
    setUnreadCount: count => dispatch(actions.setUnreadCount(count)),
    closeMediaRecoveryModal: () => dispatch(streamActions.closeMediaRecoveryModal()),
    initScreenStream: () => dispatch(streamActions.initScreenStream()),
    setCustomInputTracking: data => dispatch(actions.setCustomInputTracking(data)),
    clearCustomInputTracking: () => dispatch(actions.clearCustomInputTracking()),
    expireMeeting: (meetingId, isDeveloper) => {
      dispatch(actions.expireMeeting(meetingId, isDeveloper));
    },
  };
};

const mapStateToProps = state => {
  return {
    interview_id: state.interview.interview_id,
    interview_status: state.interview.status,
    interview_title: state.interview.title,
    interview_role: state.interview.role,
    interview_type: state.interview.interview_type,
    participants: state.interview.participants,
    start_time: state.interview.start_time,
    role: state.interview.role,
    user_id: state.auth.user_id,
    chat_data: state.events.chat_data,
    last_editor_data: state.events.last_editor_data,
    problem_id: state.events.problem_id,
    last_language: state.events.last_language,
    customInputTracking: state.events.customInputTracking,
    supported_languages: state.events.supported_languages || [],
    heartbeat: state.events.heartbeat,
    editor_view: state.meeting.editor_view,
    problemDetails: state.problem.problemDetails,
    current_page: state.meeting.current_page,
    meetingSession: state.interviewRecording.meetingSession,
    unreadCount: state.events.unreadCount,
    recoveryModalOpen: state.interviewRecording.recoveryModalOpen,
    recoveryReason: state.interviewRecording.recoveryReason,
    meeting: state.meeting,
    theme: state.events.theme, // Add theme from Redux store
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InterviewContainer));
